/* WearScheduleSection — Author the aligner wear schedule (iter95).
 *
 * Used in two surfaces:
 *  - PlanEditorInline.jsx (planner authoring CoE plan, OR doctor authoring
 *    self-plan): full edit access, `value` + `onChange` flow.
 *  - CaseDetail.jsx Plan tab (CoE doctor reviewing planner's plan): read-only
 *    overview + an "Override schedule" CTA that opens the doctor-override
 *    dialog (rendered separately by the parent).
 *
 * Three vertical sections per the mockup:
 *  1. Default wear duration — 5 chips (7/10/14/21/Custom) + custom ₹ input
 *  2. Per-aligner overrides — toggle + editable table when ON
 *  3. Estimated completion — live calc box + amber callout
 *
 * `baselineSchedule` (optional) — when provided, the section renders a side-by-
 * side "compare" view: the read-only baseline schedule on the left, the
 * editable schedule on the right (used in the doctor-override dialog).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Calculator, Eye, Info, X as XIcon, Plus } from "@phosphor-icons/react";


const DEFAULT_CHIPS = [
    { id: 7, label: "7 days" },
    { id: 10, label: "10 days" },
    { id: 14, label: "14 days", sub: "(standard)" },
    { id: 21, label: "21 days" },
];

const FALLBACK_DEFAULT = 14;


/* Pure helper — sum of wear days across N aligners given default + overrides.
 * Exported so callers (e.g. TreatmentDashboard) can reuse the same math. */
export function computeTotalWearDays(totalAligners, defaultDays, overrides) {
    const total = Math.max(0, Number(totalAligners || 0));
    const def = Math.max(1, Number(defaultDays || FALLBACK_DEFAULT));
    const ovMap = new Map();
    (overrides || []).forEach((o) => {
        const s = Number(o.stage);
        const d = Number(o.days);
        if (s >= 1 && s <= total && d >= 1) ovMap.set(s, d);
    });
    let sum = 0;
    for (let i = 1; i <= total; i++) {
        sum += ovMap.has(i) ? ovMap.get(i) : def;
    }
    return sum;
}


export function computeWearDaysForStage(stage, defaultDays, overrides) {
    const def = Math.max(1, Number(defaultDays || FALLBACK_DEFAULT));
    const hit = (overrides || []).find((o) => Number(o.stage) === Number(stage));
    if (hit && Number(hit.days) >= 1) return Number(hit.days);
    return def;
}


const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso.slice(0, 10); }
};


export default function WearScheduleSection({
    value = {},
    onChange,
    totalAligners = 0,
    fittedAt = null,
    readOnly = false,
    baselineSchedule = null,
    baselineLabel = "Planner's schedule",
    showReset = false,
    onReset,
    testidPrefix = "wear-schedule",
    stageTerminology = false,
}) {
    /* iter 97.1 — `stageTerminology=true` swaps "Aligner #" → "Stage #" and
       drops the section header (parent FieldGroup already provides one when
       this is rendered as part of the merged "Aligners & wear schedule"). */
    const trayLabel = stageTerminology ? "Stage" : "Aligner";
    const defaultDays = Number(value?.default_days ?? value?.wear_schedule_default_days ?? FALLBACK_DEFAULT);
    const overrides = Array.isArray(value?.overrides)
        ? value.overrides
        : Array.isArray(value?.wear_schedule_overrides)
        ? value.wear_schedule_overrides
        : [];

    const matchingChip = DEFAULT_CHIPS.find((c) => c.id === defaultDays);
    const [customMode, setCustomMode] = useState(!matchingChip);
    const [showOverrides, setShowOverrides] = useState(overrides.length > 0);

    useEffect(() => {
        if (overrides.length > 0 && !showOverrides) setShowOverrides(true);
    }, [overrides.length, showOverrides]);

    const emit = (next) => {
        if (onChange) onChange(next);
    };

    const setDefault = (n) => {
        emit({ default_days: n, overrides });
    };

    const pickChip = (chip) => {
        if (readOnly) return;
        setCustomMode(false);
        setDefault(chip.id);
    };

    const pickCustom = () => {
        if (readOnly) return;
        setCustomMode(true);
    };

    const setCustomValue = (raw) => {
        if (readOnly) return;
        const digits = (raw || "").replace(/[^0-9]/g, "");
        const n = digits === "" ? "" : Number(digits);
        if (n === "" || (n >= 1 && n <= 90)) {
            setDefault(n === "" ? FALLBACK_DEFAULT : n);
        }
    };

    const toggleOverrides = (next) => {
        if (readOnly) return;
        setShowOverrides(next);
        if (!next) {
            emit({ default_days: defaultDays, overrides: [] });
        }
    };

    const setOverrideForStage = (stage, days) => {
        const next = (overrides || []).filter((o) => Number(o.stage) !== Number(stage));
        const dNum = Number(days);
        if (Number.isFinite(dNum) && dNum >= 1 && dNum !== Number(defaultDays)) {
            next.push({ stage: Number(stage), days: dNum });
        }
        emit({ default_days: defaultDays, overrides: next.sort((a, b) => a.stage - b.stage) });
    };

    const removeOverride = (stage) => {
        emit({
            default_days: defaultDays,
            overrides: (overrides || []).filter((o) => Number(o.stage) !== Number(stage)),
        });
    };

    const resetAll = () => {
        emit({ default_days: defaultDays, overrides: [] });
    };

    /* Derived stats */
    const totalDays = useMemo(
        () => computeTotalWearDays(totalAligners, defaultDays, overrides),
        [totalAligners, defaultDays, overrides]
    );
    const estCompletionIso = useMemo(() => {
        if (!fittedAt || totalDays <= 0) return null;
        try {
            const d = new Date(fittedAt);
            d.setDate(d.getDate() + totalDays);
            return d.toISOString();
        } catch { return null; }
    }, [fittedAt, totalDays]);

    /* Overrides table — derive each row from total_aligners. */
    const overrideMap = useMemo(() => {
        const m = new Map();
        (overrides || []).forEach((o) => m.set(Number(o.stage), Number(o.days)));
        return m;
    }, [overrides]);

    return (
        <div data-testid={testidPrefix}>
            {/* Header — skipped when this component is embedded inside a parent
                FieldGroup that already provides one (iter 97.1 merge). */}
            {!stageTerminology && (
                <>
                    <h3 className="text-[15px] font-semibold tracking-tight text-foreground">Wear schedule</h3>
                    <p className="text-[13px] text-muted-foreground mt-0.5 mb-4">
                        Set how long the patient wears each aligner. This drives the estimated completion date shown to the doctor.
                    </p>
                </>
            )}

            {baselineSchedule ? (
                /* Compare view: 2 columns side-by-side */
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">{baselineLabel}</div>
                        <ScheduleBody
                            defaultDays={Number(baselineSchedule.default_days || baselineSchedule.wear_schedule_default_days || FALLBACK_DEFAULT)}
                            overrides={baselineSchedule.overrides || baselineSchedule.wear_schedule_overrides || []}
                            totalAligners={totalAligners}
                            readOnly={true}
                            showOverrides={(baselineSchedule.overrides || baselineSchedule.wear_schedule_overrides || []).length > 0}
                            testidPrefix={`${testidPrefix}-baseline`}
                            trayLabel={trayLabel}
                        />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wide font-bold text-primary mb-2">Your override</div>
                        <ScheduleBody
                            defaultDays={defaultDays}
                            overrides={overrides}
                            customMode={customMode}
                            showOverrides={showOverrides}
                            totalAligners={totalAligners}
                            readOnly={readOnly}
                            onPickChip={pickChip}
                            onPickCustom={pickCustom}
                            onCustomValueChange={setCustomValue}
                            onToggleOverrides={toggleOverrides}
                            onSetOverride={setOverrideForStage}
                            onRemoveOverride={removeOverride}
                            onResetAll={resetAll}
                            overrideMap={overrideMap}
                            testidPrefix={`${testidPrefix}-edit`}
                            trayLabel={trayLabel}
                        />
                    </div>
                </div>
            ) : (
                <ScheduleBody
                    defaultDays={defaultDays}
                    overrides={overrides}
                    customMode={customMode}
                    showOverrides={showOverrides}
                    totalAligners={totalAligners}
                    readOnly={readOnly}
                    onPickChip={pickChip}
                    onPickCustom={pickCustom}
                    onCustomValueChange={setCustomValue}
                    onToggleOverrides={toggleOverrides}
                    onSetOverride={setOverrideForStage}
                    onRemoveOverride={removeOverride}
                    onResetAll={resetAll}
                    overrideMap={overrideMap}
                    testidPrefix={testidPrefix}
                    trayLabel={trayLabel}
                />
            )}

            {/* Estimated completion card — sits below either layout */}
            <div className="mt-4 rounded-xl border border-border bg-card p-4" data-testid={`${testidPrefix}-est-card`}>
                <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-2">Estimated completion</div>
                <div className="rounded-lg bg-secondary/40 px-4 py-3 space-y-1.5">
                    <CalcRow k="Treatment start" v={fmtDate(fittedAt) || "Not fitted yet"} />
                    <CalcRow k={`Total ${trayLabel.toLowerCase()}s`} v={totalAligners > 0 ? String(totalAligners) : "—"} testid={`${testidPrefix}-est-aligners`} />
                    <CalcRow k="Total wear days" v={totalDays > 0 ? `${totalDays} days` : "—"} testid={`${testidPrefix}-est-totaldays`} />
                    <div className="pt-2 mt-1 border-t border-border flex items-center justify-between">
                        <span className="text-sm font-bold">Est. completion</span>
                        <span className="font-display text-base font-bold tabular-nums" data-testid={`${testidPrefix}-est-result`}>
                            {fittedAt && totalDays > 0 ? fmtDate(estCompletionIso) : (totalDays > 0 ? `${totalDays}d after fitting` : "—")}
                        </span>
                    </div>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
                    <Calculator size={13} /> Recalculates live as you adjust wear days or overrides
                </div>
                <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
                    <Eye size={13} weight="bold" className="shrink-0 mt-0.5" />
                    <p>This date will be shown to the doctor on the Treatment tab as "Est. completion" when they review and approve this plan.</p>
                </div>
            </div>

            {showReset && !readOnly && (
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        onClick={onReset}
                        className="text-xs font-semibold text-rose-700 hover:bg-rose-50 px-3 py-1.5 rounded-md transition-colors"
                        data-testid={`${testidPrefix}-reset-to-baseline`}
                    >
                        Reset to {baselineLabel.toLowerCase()}
                    </button>
                </div>
            )}
        </div>
    );
}


function ScheduleBody({
    defaultDays,
    overrides,
    customMode,
    showOverrides,
    totalAligners,
    readOnly,
    onPickChip,
    onPickCustom,
    onCustomValueChange,
    onToggleOverrides,
    onSetOverride,
    onRemoveOverride,
    onResetAll,
    overrideMap,
    testidPrefix,
    trayLabel = "Aligner",
}) {
    const trayLower = trayLabel.toLowerCase();
    return (
        <>
            {/* Card 1 — Default wear duration */}
            <div className="rounded-xl border border-border bg-card p-4 mb-3" data-testid={`${testidPrefix}-default-card`}>
                <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Default wear duration</div>
                <p className="text-xs text-muted-foreground mb-2">Applied to all {trayLower}s unless individually overridden below.</p>
                <div className="flex flex-wrap gap-1.5">
                    {DEFAULT_CHIPS.map((c) => {
                        const active = !customMode && Number(defaultDays) === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                disabled={readOnly}
                                onClick={() => onPickChip && onPickChip(c)}
                                data-testid={`${testidPrefix}-chip-${c.id}`}
                                className={
                                    "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
                                    (active
                                        ? "bg-emerald-100 text-emerald-900 border-emerald-300 ring-1 ring-emerald-300"
                                        : "bg-card text-foreground border-border hover:border-foreground/30")
                                }
                            >
                                {c.label}
                                {c.sub && <span className="ml-1 opacity-70 text-[10px]">{c.sub}</span>}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        disabled={readOnly}
                        onClick={onPickCustom}
                        data-testid={`${testidPrefix}-chip-custom`}
                        className={
                            "rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
                            (customMode
                                ? "bg-emerald-100 text-emerald-900 border-emerald-300 ring-1 ring-emerald-300"
                                : "bg-card text-foreground border-border hover:border-foreground/30")
                        }
                    >
                        Custom
                    </button>
                </div>
                {customMode && !readOnly && (
                    <div className="mt-3 flex items-center gap-2">
                        <Input
                            type="number"
                            min="1"
                            max="90"
                            step="1"
                            value={defaultDays}
                            onChange={(e) => onCustomValueChange && onCustomValueChange(e.target.value)}
                            className="h-9 w-24 font-mono"
                            data-testid={`${testidPrefix}-custom-input`}
                        />
                        <span className="text-xs text-muted-foreground">days</span>
                    </div>
                )}
            </div>

            {/* Card 2 — Per-stage overrides */}
            <div className="rounded-xl border border-border bg-card p-4" data-testid={`${testidPrefix}-overrides-card`}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5">Per-{trayLower} overrides</div>
                        <p className="text-xs text-muted-foreground">Override wear days on specific {trayLower}s — e.g. slower phase or attachment-heavy steps.</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">{showOverrides ? "On" : "Off"}</span>
                        <Switch
                            checked={showOverrides}
                            onCheckedChange={onToggleOverrides}
                            disabled={readOnly}
                            data-testid={`${testidPrefix}-overrides-toggle`}
                        />
                    </div>
                </div>

                {!showOverrides && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2 text-[11px] text-teal-900">
                        <Info size={13} weight="bold" className="shrink-0 mt-0.5" />
                        <p>
                            When off, all {totalAligners > 0 ? totalAligners : "your"} {trayLower}s use the default wear duration.
                            Enable to set different days on individual {trayLower}s.
                        </p>
                    </div>
                )}

                {showOverrides && (
                    <div className="mt-3 space-y-3">
                        {totalAligners > 0 ? (
                            <>
                                <div className="grid grid-cols-3 gap-3 rounded-lg bg-secondary/40 px-3 py-2">
                                    <Stat label={`Total ${trayLower}s`} value={totalAligners} />
                                    <Stat label="Overridden" value={overrides.length} />
                                    <Stat label="Total days" value={computeTotalWearDays(totalAligners, defaultDays, overrides)} />
                                </div>
                                <div className="overflow-x-auto max-h-72 rounded-lg border border-border">
                                    <table className="w-full text-sm">
                                        <thead className="bg-secondary/40 sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2 text-[10px] uppercase font-bold tracking-wide text-muted-foreground" style={{ width: 90 }}>{trayLabel}</th>
                                                <th className="text-left px-3 py-2 text-[10px] uppercase font-bold tracking-wide text-muted-foreground">Wear days</th>
                                                <th className="text-left px-3 py-2 text-[10px] uppercase font-bold tracking-wide text-muted-foreground">Status</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.from({ length: totalAligners }, (_, i) => i + 1).map((stage) => {
                                                const override = overrideMap.get(stage);
                                                const effective = override ?? defaultDays;
                                                return (
                                                    <tr key={stage} className="border-t border-border" data-testid={`${testidPrefix}-row-${stage}`}>
                                                        <td className="px-3 py-1.5 font-mono text-xs">{trayLabel === "Stage" ? `Stage ${stage}` : `#${stage}`}</td>
                                                        <td className="px-3 py-1.5">
                                                            <Input
                                                                type="number"
                                                                min="1"
                                                                max="90"
                                                                step="1"
                                                                value={effective}
                                                                onChange={(e) => onSetOverride && onSetOverride(stage, e.target.value)}
                                                                disabled={readOnly}
                                                                className="h-8 w-20 font-mono text-xs"
                                                                data-testid={`${testidPrefix}-input-${stage}`}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-1.5">
                                                            {override ? (
                                                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
                                                                    Overridden
                                                                </span>
                                                            ) : (
                                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Default</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-1.5 text-right">
                                                            {override !== undefined && !readOnly && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => onRemoveOverride && onRemoveOverride(stage)}
                                                                    className="text-rose-600 hover:bg-rose-50 p-1 rounded transition-colors"
                                                                    title="Reset to default"
                                                                    data-testid={`${testidPrefix}-remove-${stage}`}
                                                                >
                                                                    <XIcon size={12} weight="bold" />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                {!readOnly && overrides.length > 0 && (
                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={onResetAll}
                                            className="h-8 text-xs"
                                            data-testid={`${testidPrefix}-reset-all`}
                                        >
                                            Reset all to default
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="text-xs text-muted-foreground italic">
                                Fill in upper / lower {trayLower} counts above to set per-{trayLower} overrides.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}


function CalcRow({ k, v, testid }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-foreground/80">{k}</span>
            <span className="tabular-nums font-medium" data-testid={testid}>{v}</span>
        </div>
    );
}


function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">{label}</div>
            <div className="font-display text-base font-bold tabular-nums">{value}</div>
        </div>
    );
}
